/**
 * @keystone/progression types (PLAN §11). Pure data, JSON-serialisable (no classes, no Maps,
 * no Dates). Everything that changes a character in combat is expressed as a `ClassTransform`
 * applied to the sim's own `ClassDef` / `AbilityDef` / `AuraDef`, so the sim never learns about
 * items or talents.
 */
import type {
  AbilityDef,
  AbilityId,
  AuraDef,
  AuraId,
  BotSkillPreset,
  CastKind,
  ClassDef,
  ClassId,
  DungeonId,
  EffectSpec,
  EffectType,
  ResourceDef,
  ResourceId,
  Role,
  Stats,
  StatMod,
  Targeting,
  Yards,
  Ms,
} from '@keystone/sim';
import type { PersonaId } from '@keystone/content';

// ============================================================================
// 1. Gear
// ============================================================================

export type ItemSlot = 'weapon' | 'helm' | 'chest' | 'legs' | 'gloves' | 'boots' | 'ring' | 'trinket';
export const ITEM_SLOTS: readonly ItemSlot[] = ['weapon', 'helm', 'chest', 'legs', 'gloves', 'boots', 'ring', 'trinket'];

/**
 * Secondary stats. `expertise` is a class-specific amplifier (mastery-like, see
 * `ClassStatProfile.expertise`); `spirit` is cooldown recovery + resource refunds.
 */
export type SecondaryStat = 'haste' | 'crit' | 'expertise' | 'spirit';
export const SECONDARY_STATS: readonly SecondaryStat[] = ['haste', 'crit', 'expertise', 'spirit'];

export type ItemId = string;
export type LegendaryId = string;
export type TrinketEffectId = string;
export type SetId = string;
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
/** Every rarity, lowest first (crafting bands climb in this order). */
export const RARITIES: readonly Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

/** Rating points of one secondary on one item. */
export interface SecondaryRoll {
  stat: SecondaryStat;
  rating: number;
  /** Stat quality 0.90–1.10 rolled at craft (crafting.md §3.6). Missing = 1.0 (old items). */
  quality?: number;
}

export interface Item {
  id: ItemId;
  slot: ItemSlot;
  ilvl: number;
  rarity: Rarity;
  /** Exactly 2 secondaries (PLAN §11). Rating budget scales with ilvl (items.secondaryBudget). */
  secondaries: SecondaryRoll[];
  /** Number of gem sockets (0–2). */
  sockets: number;
  /** Gems in sockets; index = socket index, null = empty. length === sockets. */
  gems: (Gem | null)[];
  legendaryId?: LegendaryId;
  trinketEffectId?: TrinketEffectId;
  setId?: SetId;
  /** Index of the secondary that was reforged (only one reforge per item). */
  reforged?: { index: number; from: SecondaryStat };
  /** How the item entered the profile (loot reveal / history UI). */
  source?: 'starter' | 'run' | 'vault' | 'bonus' | 'craft';
  /**
   * Masterwork polish (0..MASTERWORK_MAX, upgrade.ts): +1 effective item level per step, bought
   * with gold + Shards once the item is at the tier's upgrade cap. Missing = 0.
   */
  masterwork?: number;
  /** Altar Forge state (tempering pity, Awakenings, gilding). Missing = all zero. */
  forge?: ItemForgeState;
}

/** Per-item forge state (crafting.md §4.2). */
export interface ItemForgeState {
  /** Successful temper steps taken (Awakening every AWAKEN_EVERY). */
  steps: number;
  /** Consecutive failed attempts (failstack, +10 pp each). Reset on success. */
  fails: number;
  /** Resonance 0..100; at 100 the next attempt is guaranteed. Reset on success. */
  resonance: number;
  /** Net Coil Scales put into the item by tempering (salvage refunds half). */
  invested: number;
  /** Secondary indexes that were Empowered (+15% rating) by an Awakening. */
  empowered?: number[];
  /** Cosmetic Gilding rank 0..GILD_MAX. */
  gilding?: number;
}

/** Equipped item ids per slot (ids index Profile.inventory). */
export type GearSet = Partial<Record<ItemSlot, ItemId>>;
/** Resolved gear (what compile/gem math take). */
export type EquippedGear = Partial<Record<ItemSlot, Item>>;

// ============================================================================
// 2. Gems
// ============================================================================

export type GemColor = 'ruby' | 'sapphire' | 'emerald';
export const GEM_COLORS: readonly GemColor[] = ['ruby', 'sapphire', 'emerald'];

export interface Gem {
  color: GemColor;
  /** 1..MAX_GEM_RANK. Higher ranks gated by tier (`RankTierDef.maxGemRank`). */
  rank: number;
}

/** A Gem Power threshold bonus (ruby offense, sapphire defense/utility, emerald sustain). */
export interface GemPowerBonus {
  id: string;
  color: GemColor;
  /** Total Gem Power of this colour across gear needed (e.g. 120 / 240 / 360 / 480). */
  threshold: number;
  /** null = every class; otherwise only that class gets the bonus. */
  classId: ClassId | null;
  name: string;
  description: string;
  effect: ClassTransform[];
}

/** Gem inventory: count by colour+rank, key `${color}:${rank}`. */
export type GemStash = Record<string, number>;

// ============================================================================
// 3. Transforms (talents, gem bonuses, legendaries, trinkets all use these)
// ============================================================================

/** Which effect list of an ability a transform targets. */
export type EffectList = 'effects' | 'channelStartEffects' | 'channelEndEffects'; // +stormcaller: rotation rework (F10)

/**
 * One edit to a ClassDef. Applied in order by `applyTransforms` (transforms.ts). Every op
 * names its target by id; an op whose target is missing is a validation error, not a no-op.
 * PLAN §11: talents "change a button or add a proc", never a plain +%.
 */
export type ClassTransform =
  /** Shallow-merge scalar fields of an ability (cast, range, gcdMs, canCastWhileMoving...). */
  | { op: 'patchAbility'; abilityId: AbilityId; patch: AbilityPatch }
  /** Append effects ("Shield Slam also interrupts", "Flash Heal leaves a HoT"). */
  | {
      op: 'addEffects';
      abilityId: AbilityId;
      effects: EffectSpec[];
      list?: EffectList;
      /** 'start' prepends (runs before the ability consumes its own stacks/procs). Default: append. */
      position?: 'start' | 'end';
    }
  /** Remove effects of a type (optionally only the n-th match). */
  | { op: 'removeEffects'; abilityId: AbilityId; effectType: EffectType; index?: number; list?: EffectList }
  /** Multiply Amount terms of matching top-level effects ("−20% damage"). */
  | {
      op: 'scaleAmount';
      abilityId: AbilityId;
      effectType: 'damage' | 'heal' | 'absorb';
      mult: number;
      list?: EffectList;
      /** Also scale matching effects nested in `conditional` then/else branches. Default false. */
      deep?: boolean;
    }
  /** Change area caps/size on area effects, and the shape of zone/telegraph effects ("+2 targets", "+30% radius"). */
  | { op: 'modifyArea'; abilityId: AbilityId; maxTargetsDelta?: number; radiusMult?: number; list?: EffectList }
  /** Static cooldown change: set or shift the duration, set charges, or remove the cooldown. */
  | { op: 'modifyCooldown'; abilityId: AbilityId; durationMs?: Ms; deltaMs?: Ms; charges?: number; remove?: boolean }
  /** Change how the ability picks its target. */
  | { op: 'changeTargeting'; abilityId: AbilityId; targeting: Targeting; range?: Yards; requiresFacing?: boolean }
  /** Change cast kind (instant / cast time / channel). */
  | { op: 'changeCast'; abilityId: AbilityId; cast: CastKind }
  /** Change resource costs/gains (replaces the entry for that resource; amount 0 removes it). */
  | { op: 'modifyResource'; abilityId: AbilityId; field: 'cost' | 'gain'; resource: ResourceId; amount: number }
  /** Deep-ish patch of an inline AuraDef by id, wherever it occurs in the class (abilities + passives). */
  | { op: 'patchAura'; auraId: AuraId; patch: AuraPatch }
  /** Add a permanent passive aura (procs, mods). Compile forces `persistThroughDeath`. */
  | { op: 'addPassive'; aura: AuraDef }
  /** Append stat mods to an existing aura (patchAura would replace the whole `mods` array). */
  | { op: 'addAuraMods'; auraId: AuraId; mods: StatMod[] }
  /** Add a proc to an existing passive or inline aura. */
  | { op: 'addProc'; auraId: AuraId; proc: NonNullable<AuraDef['procs']>[number] }
  /** Add a new, unbound ability (goes to `extraAbilities`; bots ignore it unless their kit knows it). */
  | { op: 'addAbility'; ability: AbilityDef }
  /** Patch a class resource (max, regen...). */
  | { op: 'patchResource'; resourceId: ResourceId; patch: Partial<Omit<ResourceDef, 'id'>> }
  /**
   * Register a standalone aura referenced only by id (e.g. the shield of an `absorb` effect,
   * which the registry cannot discover from an effect tree). Goes to ContentBundle.auras.
   */
  | { op: 'registerAura'; aura: AuraDef };

/** Fields a transform may overwrite on an AbilityDef (id/effects have their own ops). */
export type AbilityPatch = Partial<
  Pick<
    AbilityDef,
    | 'name'
    | 'description'
    | 'gcdMs'
    | 'range'
    | 'melee'
    | 'requiresFacing'
    | 'canCastWhileMoving'
    | 'usableWhileCasting'
    | 'usableIf'
    | 'flatThreat'
    | 'school'
  >
>;

export type AuraPatch = Partial<Omit<AuraDef, 'id'>>;

// ============================================================================
// 4. Legendaries, trinkets, sets
// ============================================================================

export interface LegendaryDef {
  id: LegendaryId;
  name: string;
  classId: ClassId;
  slot: ItemSlot;
  description: string;
  effect: ClassTransform[];
  /** Gem Power multiplier for this item's socket (PLAN §11: 2×). */
  socketPowerMult: number;
}

export interface TrinketEffectDef {
  id: TrinketEffectId;
  name: string;
  description: string;
  /** Usually an `addPassive` proc aura, or `addAbility` for an on-use. Scales with ilvl via `{ilvlScaled}` amounts. */
  effect: ClassTransform[];
  /** Restrict to roles (empty/undefined = any). */
  roles?: Role[];
}

export interface SetBonusDef {
  id: SetId;
  name: string;
  /** pieces → effect, e.g. { 2: [...], 4: [...] }. */
  bonuses: Record<number, ClassTransform[]>;
}

/** The five tier slots (crafting.md §5.1). */
export type TierSlot = 'helm' | 'chest' | 'legs' | 'gloves' | 'boots';
export const TIER_SLOTS: readonly TierSlot[] = ['helm', 'chest', 'legs', 'gloves', 'boots'];

/** Player-facing text of one set bonus. */
export interface SetBonusText {
  name: string;
  description: string;
}

/**
 * A class tier set (crafting.md §5). `bonuses` target the rotation-rework kit; when the class
 * def does not have those ids yet (or a bonus needs a sim feature that has not landed, see
 * `needs`), `interim` (built on today's kit) is used for that piece count instead, so a set is
 * always felt. `sets.ts` picks the variant per piece count.
 */
export interface TierSetDef extends SetBonusDef {
  classId: ClassId;
  pieceNames: Record<TierSlot, string>;
  text: Record<number, SetBonusText>;
  /** Sim/contract features a bonus needs (by piece count); unmet → interim is used. */
  needs?: Record<number, string[]>;
  interim: { bonuses: Record<number, ClassTransform[]>; text: Record<number, SetBonusText> };
}

// ============================================================================
// 5. Rating and ranks
// ============================================================================

export type RankTier = 'novice' | 'contender' | 'champion' | 'paragon' | 'eternal';
export const RANK_TIER_ORDER: readonly RankTier[] = ['novice', 'contender', 'champion', 'paragon', 'eternal'];

export type Unlock = 'gems' | 'reforge' | 'legendaries' | 'capstones' | 'highGemRanks' | 'maxUpgrade';

export interface RankTierDef {
  tier: RankTier;
  name: string;
  minRating: number;
  /** 3–5 sub-ranks for frequent feedback (sub-rank 1 = lowest). */
  subRanks: number;
  /** Rating span per sub-rank for the open-ended top tier. */
  openSubRankSpan?: number;
  /** Highest item level that can drop / be upgraded to at this tier. */
  maxIlvl: number;
  /** Highest gem rank that can drop / be fused. */
  maxGemRank: number;
  /** Talent tiers usable (1-based). */
  talentTiers: number;
  /** Chance an eligible drop is legendary (0 below Champion). */
  legendaryChance: number;
  /** Unlocks granted on entering the tier; `gems` is granted at a specific sub-rank (Contender 3). */
  unlocks: { unlock: Unlock; atSubRank?: number }[];
}

export interface RankStanding {
  tier: RankTier;
  subRank: number;
  rating: number;
  /** Rating needed for the next sub-rank (null at the open top). */
  nextAt: number | null;
  unlocks: Unlock[];
}

/** Best timed result per dungeon, plus the best untimed (shown, not scored). */
export interface RatingRecord {
  dungeonId: DungeonId;
  /** Best TIMED run (counts toward rating). */
  best: { level: number; score: number; elapsedMs: Ms; timerMs: Ms; runIndex: number } | null;
  /** Best attempt regardless of timing (UI). */
  bestAttempt: { level: number; timed: boolean; runIndex: number } | null;
}

// ============================================================================
// 6. Talents
// ============================================================================

export type TalentId = string;

export interface TalentNode {
  id: TalentId;
  classId: ClassId;
  name: string;
  description: string;
  /** 1..3; tier t unlocks at TALENT_TIER_GATES[t-1] points spent. */
  tier: 1 | 2 | 3;
  /** Grid position (UI). */
  col: number;
  /** 1–3 points. */
  cost: number;
  /** Must own all of these first. */
  prereqs: TalentId[];
  /** Mutually exclusive group (tier-3 capstones share one). */
  choiceGroup?: string;
  /** Always 1 for now (no multi-rank nodes). */
  maxRank: 1;
  effect: ClassTransform[];
}

export interface TalentLoadout {
  id: string;
  name: string;
  classId: ClassId;
  nodes: TalentId[];
}

export interface HeroProgress {
  /** 1..MAX_HERO_LEVEL. */
  level: number;
  /** XP into the current level. */
  xp: number;
  /** Points earned = level (1 per level). */
  talentPoints: number;
  /**
   * +specs: the specialisation chosen at the Tower (docs/design/classes/specs.md); null until then.
   * Save v8+ (older saves migrate to null).
   */
  specId?: string | null;
}

export type LoadoutError =
  | { kind: 'unknownNode'; nodeId: TalentId }
  | { kind: 'wrongClass'; nodeId: TalentId }
  | { kind: 'duplicate'; nodeId: TalentId }
  | { kind: 'missingPrereq'; nodeId: TalentId; prereq: TalentId }
  | { kind: 'tierLocked'; nodeId: TalentId; tier: number; spentBefore: number; needed: number }
  | { kind: 'rankLocked'; nodeId: TalentId; tier: number }
  | { kind: 'choiceConflict'; nodeId: TalentId; group: string }
  | { kind: 'overBudget'; spent: number; available: number };

// ============================================================================
// 7. Per-class stat profile
// ============================================================================

export interface ClassStatProfile {
  classId: ClassId;
  /** Secondary priority, best first (bot auto-equip, reforge advice). */
  priority: SecondaryStat[];
  /** What expertise does for this class: aura mods whose `value` is multiplied by the expertise fraction. */
  expertise: StatMod[];
}

// ============================================================================
// 8. Profile
// ============================================================================

export interface CharacterProgress {
  classId: ClassId;
  hero: HeroProgress;
  gear: GearSet;
  loadouts: TalentLoadout[];
  activeLoadoutId: string;
  /** Gem auto-setup for bots (true = re-gem automatically when gear changes). */
  autoManaged: boolean;
  /** Companion resources (crafting.md §11): paid each run, spent by the auto-forge. v3+. */
  satchel?: Satchel;
  /** +grid (v13): the Waystone Grid (grid.ts): allocated stars, opened locks, claimed key sources. */
  grid?: GridState;
}

/** +grid: a character's Waystone Grid (docs/design/waystone-grid.md). Points and most keys are derived. */
export interface GridState {
  /** Allocated node ids in allocation order (the class gate is implicit and free). */
  nodes: string[];
  /** Opened lock nodes (permanent; each cost one Grid Key). */
  locks: string[];
  /** Claimed event key sources ('rare:<npcId>', 'secret:<mapId>:<id>'); quest / dungeon / Tower keys are derived. */
  keyLog: string[];
}

/** A companion's own forge purse and pity (bot crafts never touch the player's pity). */
export interface Satchel {
  fangsteel: number;
  scales: Record<ScaleGrade, number>;
  /** Venom / Moltskin gifted by the player (bots never earn these). */
  venom: number;
  moltskin: number;
  sinceEpic: number;
  belowRare: number;
}

export interface VaultState {
  /** Runs counted toward the next vault (0..VAULT_EVERY-1). */
  runsInWindow: number;
  /** Key levels of runs in the current window (vault ilvl from the best ones). */
  windowLevels: number[];
  /**
   * @deprecated v2 item vault. Always null from save v3 on (migrated into a Vault Forge Token);
   * kept so old screens still type-check. Use `offer`.
   */
  pending: { forClassId: ClassId; options: Item[] } | null;
  /** Unclaimed resource vault (pick 1 of 3, crafting.md §2.4), or null. v3+. */
  offer?: VaultOffer | null;
}

export type VaultOptionKind = 'scales' | 'token' | 'altar';

export interface VaultOption {
  kind: VaultOptionKind;
  /** Resources granted on claim ('token' grants `token` instead, bundle is empty). */
  bundle: ResourceBundle;
  token?: VaultForgeToken;
}

export interface VaultOffer {
  /** Grade of the best key in the window (sets the Scale Cache / token grade). */
  grade: ScaleGrade;
  bestLevel: number;
  options: VaultOption[];
}

export interface RunRecord {
  /** Monotonic run counter in this profile (no Date in logic). */
  runIndex: number;
  dungeonId: DungeonId;
  level: number;
  timed: boolean;
  completed: boolean;
  chest: 0 | 1 | 2 | 3;
  elapsedMs: Ms;
  timerMs: Ms;
  deaths: number;
  score: number;
  /** Party composition and who was a bot, with the bot preset recorded (PLAN §11: no penalty yet). */
  party: { classId: ClassId; controller: 'human' | 'bot' }[];
  botPreset: BotSkillPreset | null;
  seed: number;
  /** Item ids granted by this run. */
  loot: ItemId[];
  shards: number;
  /** Hero XP each party member got (kills + bonuses). Missing on pre-v2 records. */
  xp?: number;
  /** Gold earned (kills + bonuses). Missing on pre-v2 records. */
  gold?: number;
  /** Resources the run paid (v3+). `loot` stays for old records (ids may now be salvaged). */
  payout?: RunPayout;
}

export interface Currencies {
  /** @deprecated Shards became Fangsteel in save v3 (always 0 from then on). */
  shards: number;
  /** From kills and run bonuses (rewards.ts); spent on upgrades, reforges, fusing, masterwork. */
  gold: number;
}

/**
 * v2: gold (Currencies.gold, RunRecord.xp/gold, Item.masterwork).
 * v3: the Altar Forge (wallet, forge, satchels, vault offers, item forge state).
 * v4: the Guild Hall (idle roster of personas recruited from Group Finder strangers, idle jobs; docs/design/idle.md).
 * v5: one character (docs/design/zones.md): `character`, `legacy` (the whole v4 save), no Guild Hall.
 */
/** v9: Mossvale v2 (the 1–10 journey, 864 × 810 yd): positions reset to town, chain + waystones. */
/** +quests: v11 adds HeroCharacter.quests (side quests). */
export const PROFILE_VERSION = 13; // +grid: v13 adds CharacterProgress.grid (the Waystone Grid); +prof: v10 adds HeroCharacter.prof; +quests: v11 adds HeroCharacter.quests; +spawn: v12 remaps the main quest (Granny Pell first)

// ============================================================================
// +field: the single character (docs/design/zones.md Phase 1, save v5)
// ============================================================================

/** Bounty progress (light quests): the zone intro chain plus accepted repeatables. */
export interface BountyState {
  /** Intro chain: index of the current step per zone (steps.length = chain done). */
  chain: Record<string, number>;
  /** Progress on the current chain step per zone (kills so far; 0 for talk / go-to steps). */
  chainProgress: Record<string, number>;
  /** Accepted repeatable bounties: bounty id → kills so far (≥ count = ready to turn in). */
  active: Record<string, number>;
  /** Turn-ins so far per repeatable bounty id. */
  completed: Record<string, number>;
}

/** Anti-cheat budget for client-reported field kills (server clock; progression/leveling.ts). */
export interface KillReportBudget {
  /** Server time (ms) of the last accepted report; null before the first. */
  lastMs: number | null;
  /** Kills still allowed right now (refills at FIELD_KILLS_PER_SEC up to FIELD_KILL_BURST). */
  budget: number;
}

/** The player's one character: who they are and where they stand. Level / XP live on characters[classId].hero. */
export interface HeroCharacter {
  /** Display name (sanitised: leveling.ts sanitizeHeroName). */
  name: string;
  classId: ClassId;
  /** Look preset index (0 = the class's default model). */
  look: number;
  /** Zone id ('mossvale'). */
  zoneId: string;
  /** Current map (a field / town DungeonDef id). */
  mapId: string;
  /** Last saved position on `mapId` (null = arrive at the map entrance). */
  pos: { x: number; y: number } | null;
  facing: number;
  bounties: BountyState;
  /** Lifetime field kills. */
  kills: number;
  report: KillReportBudget;
  /** Normal-difficulty dungeon clears by dungeon id (first clear pays the big reward). */
  dungeonClears: Record<string, number>;
  /**
   * +field: anti-cheat budget for client-reported ground pickups (XP wisps, gold, gear drops),
   * refilled on the server clock (field.ts PICKUP_RATES). Absent on older v5 saves (= fresh).
   */
  pickupBudget?: { lastMs: number | null; xp: number; gold: number; items: number };
  /** Server time (ms) the current normal dungeon was entered (clear-time sanity check); null = none. */
  dungeonEnteredMs: number | null;
  /** +field: which levelled dungeon `dungeonEnteredMs` is for (null / missing = none). */
  dungeonEnteredId?: string | null;
  /** +zone: discovered areas (`mapId:areaId`); each pays its discovery bonus once. */
  discovered?: string[];
  /** +zone: attuned waystones (`mapId:waystoneId`): the waystone panel's destinations. */
  waystones?: string[];
  /** +prof (v10): Foraging / Mining skills and the gather anti-cheat state (professions.ts). */
  prof?: ProfessionState;
  /** +quests (v11): side quests taken from the zone's locals (sidequests.ts). */
  quests?: SideQuestState;
}

/** +quests: the character's side quests (docs/design/zones.md "Side quests"). */
export interface SideQuestState {
  /** Accepted quests: id → kills / items so far (talk quests: 0, ready at once). */
  active: Record<string, number>;
  /** Turned-in quests: id → turn-ins (side quests are done once). */
  done: Record<string, number>;
}

/** +prof: the character's gathering professions (docs/design/professions.md). */
export interface ProfessionState {
  /** Skill per profession (1..PROF_SKILL_CAP). */
  skills: { foraging: number; mining: number };
  /** `mapId:nodeId` → server ms of the last accepted gather there (per-node respawn floor; pruned). */
  nodeAt: Record<string, number>;
  /** Server-clock token bucket for gathers (GATHER_RATE). */
  budget: { lastMs: number | null; tokens: number };
  /** Lifetime accepted gathers (the RNG salt; character sheet). */
  gathers: number;
  /** Lifetime gathers per node kind. */
  byKind: Record<string, number>;
}

export interface Profile {
  /** Save schema version (migrations.ts). */
  version: number;
  /** Monotonic id counter for items/runs (deterministic ids). */
  nextId: number;
  /** Profile RNG seed state (loot is deterministic per profile + run). */
  seed: number;
  /** The human's class (before character creation: the class preselected on the creation screen). */
  mainClassId: ClassId;
  /**
   * +field (v5): the player's one character, or null before character creation. Its gear, talents,
   * level and XP are `characters[character.classId]`.
   */
  character: HeroCharacter | null;
  /** v5: at most the one character's progress (tools such as the harness may hold one per class). */
  characters: Record<ClassId, CharacterProgress>;
  inventory: Item[];
  gems: GemStash;
  currencies: Currencies;
  vault: VaultState;
  runs: RunRecord[];
  rating: Record<DungeonId, RatingRecord>;
  settings: {
    version: number;
    defaultBotPreset: BotSkillPreset;
    /** Spend spare Sigils on companion sets. null/undefined = auto (on once the main has 4pc). */
    shareSigils?: boolean | null;
  };
  /** Forge resources (v3+; newProfile and the v2→v3 migration always set it). */
  wallet: Wallet;
  /** Forge pity, daily/weekly/bracket tracking, codex (v3+). */
  forge: ForgeState;
  /**
   * +field (v5): the whole pre-v5 save (deep copy of the v4 JSON, Guild Hall included) so nothing is
   * destroyed; null for profiles created on v5.
   */
  legacy: Record<string, unknown> | null;
}

// ============================================================================
// 8b. The Altar Forge (crafting.md)
// ============================================================================

/** Coil Scale grades, lowest first. */
export type ScaleGrade = 'ashen' | 'jade' | 'gilded' | 'eternal';
export const SCALE_GRADES: readonly ScaleGrade[] = ['ashen', 'jade', 'gilded', 'eternal'];

/** A free craft from the vault: no Fangsteel/Scales/gold, Rare floor, +6 Luck. */
export interface VaultForgeToken {
  grade: ScaleGrade;
}

export interface Wallet {
  fangsteel: number;
  scales: Record<ScaleGrade, number>;
  venom: number;
  moltskin: number;
  /** Serpent's Eye fragments (EYE_FRAGMENTS_PER_EYE = 1 Eye). */
  eyeFragments: number;
  sigils: number;
  /** Blessed Charges (cap BLESSED_CAP). */
  blessed: number;
  vaultTokens: VaultForgeToken[];
}

/** A bag of resources (payouts, refunds, costs). Missing = 0. */
export interface ResourceBundle {
  fangsteel?: number;
  scales?: Partial<Record<ScaleGrade, number>>;
  venom?: number;
  moltskin?: number;
  eyeFragments?: number;
  sigils?: number;
  blessed?: number;
  gold?: number;
}

export interface ForgeHistoryEntry {
  kind: 'craft' | 'temper' | 'masterwork' | 'drill' | 'gild';
  /** Craft: result rarity. Others: 'success' | 'surge' | 'fail'. */
  result: string;
  nearMiss: boolean;
}

export interface ForgeState {
  /** Forge RNG cursor: every roll uses profileRng(p, FORGE_SALT ^ rollIndex), then ++. */
  rollIndex: number;
  /** Player crafts so far (Fortune = every FORTUNE_EVERY-th). */
  craftCount: number;
  /** Crafts since the last Epic+ (Epic pity; new profiles start at EPIC_PITY_START). */
  sinceEpic: number;
  /** Consecutive crafts below Rare (Rare floor after RARE_FLOOR_AFTER). */
  belowRare: number;
  /** Consecutive Eye crafts that missed Legendary. */
  eyeMisses: number;
  /** Day number (dayNumber of the caller's local date) of the last Daily Offering. */
  daily: { lastClaimDay: number | null };
  weekly: { isoWeek: string | null; runs: number; claimed: boolean };
  /** First timed clears per dungeon per bracket (each paid a Sigil once). */
  brackets: Record<DungeonId, ScaleGrade[]>;
  /** Codex entry ids, sorted. */
  codex: string[];
  /** Keep-or-replace stat refine waiting for a decision. */
  pendingRefine: { itemId: ItemId; qualities: number[]; /** +forge-sinks: Sigil reroll's new stats (absent = qualities only). */ stats?: SecondaryStat[] } | null;
  /** +forge-sinks: free temper / masterwork attempts left (new profiles start with 1: the first one is on the house). */
  freeTempers?: number;
  /** Last FORGE_HISTORY_MAX results, newest last (the history strip). */
  history: ForgeHistoryEntry[];
  /** One-time v2→v3 migration summary for the web client (null once dismissed / for new profiles). */
  migrationReport: MigrationReport | null;
}

export interface MigrationReport {
  salvagedItems: number;
  gained: ResourceBundle;
  bracketSigils: number;
}

/** What a run paid, by source (the Offering reveal). */
export interface RunPayout {
  /** Grade of the run's Coil Scales. */
  grade: ScaleGrade;
  /** Completion + chest + boss lines for the human. */
  base: ResourceBundle;
  /** World finds the caller reported (breakables, caches, eggs). */
  world: ResourceBundle;
  /** Daily Offering (null when not claimed by this run). */
  daily: { sigils: number; bonus: ResourceBundle; favoredSlot: ItemSlot } | null;
  /** First timed clear of this dungeon's bracket (null when not new). */
  bracket: { grade: ScaleGrade; bundle: ResourceBundle } | null;
  /** Sum of every line above (what went into the wallet). */
  total: ResourceBundle;
  /** Fangsteel + Scales paid into each companion satchel. */
  satchel: ResourceBundle;
  /** Weekly Ritual progress after this run. */
  weekly: { isoWeek: string; runs: number; ready: boolean } | null;
  /** +world-resources: what the player broke/opened ("Found in the dungeon": 23/88 broken, 2/6 caches). */
  worldStats?: { broken: number; breakables: number; cachesOpened: number; caches: number; nodes: number; nodesBroken: number; pickups: number };
}

// ============================================================================
// 8c. Idle model leftovers (docs/design/idle.md; the Guild Hall roster/jobs were removed in v5)
// ============================================================================

/** What a refiner made (idle.constants refine recipes; kept for the Phase 3 offline model). */
export type RefineRecipe = 'combine-ashen' | 'combine-jade' | 'combine-gilded' | 'distill' | 'cure';

/** One line of the idle log ("+5 Venom Altar timed (2 chest) in 19:42"). */
export interface IdleLogEntry {
  /** Server-clock ms the event happened at. */
  t: number;
  kind:
    | 'joined'
    | 'timed'
    | 'depleted'
    | 'abandoned'
    | 'ragequit'
    | 'afkkick'
    | 'afk'
    | 'goblin'
    | 'clown'
    | 'leader'
    | 'mistake'
    | 'grudge'
    | 'disband'
    | 'levelup'
    | 'farm'
    | 'gather'
    | 'refine'
    | 'stalled'
    | 'capped';
  /** Persona ids involved (first = the subject). */
  actors: PersonaId[];
  text: string;
  /** Job this belongs to. */
  jobId?: string;
}

// ============================================================================
// 9. Loot
// ============================================================================

export interface LootContext {
  level: number;
  timed: boolean;
  chest: 0 | 1 | 2 | 3;
  /** Rank tier of the profile (caps item level, gates legendaries). */
  tier: RankTier;
  /** Recipient per party slot (bots included: everyone gets 1 item). */
  recipients: { classId: ClassId; role: Role }[];
  /** Id allocator (keeps item ids deterministic). */
  nextId: () => ItemId;
}

export interface RunLoot {
  /** One item per recipient, same order. */
  items: Item[];
  /** Bonus-roll item for the human on a timed run (null on miss / untimed). */
  bonus: Item | null;
  shards: number;
  gems: Gem[];
}

// ============================================================================
// 10. Compile output
// ============================================================================

/** Secondary fractions after rating conversion (what the sim sees). */
export interface SecondaryFractions {
  haste: number;
  crit: number;
  expertise: number;
  spirit: number;
}

export interface CompiledCharacter {
  classId: ClassId;
  /**
   * The patched ClassDef to inject into the ContentBundle in place of the original.
   * `baseStats` ARE the final stats and `maxHp` the final HP; gear/gem/talent auras are
   * already appended to `passives` (with persistThroughDeath).
   */
  classDef: ClassDef;
  /** Final unbuffed stats (== classDef.baseStats). */
  stats: Stats;
  maxHp: number;
  /** Auras compile added to `classDef.passives` (for UI / debugging). */
  extraAuras: AuraDef[];
  /** Standalone auras to append to ContentBundle.auras (from `registerAura` ops). */
  registerAuras: AuraDef[];
  /** Breakdown for the character sheet. */
  sheet: {
    avgIlvl: number;
    powerMult: number;
    secondaries: SecondaryFractions;
    gemPower: Record<GemColor, number>;
    gemBonuses: string[];
    talents: TalentId[];
    legendaries: LegendaryId[];
    trinkets: TrinketEffectId[];
    /** Tier sets worn: pieces, active bonus thresholds, and which variant applied. */
    sets: { setId: SetId; pieces: number; active: number[]; variant: Record<number, 'rework' | 'interim'> }[];
    /** +grid: Waystone Grid stars compiled in (allocation order). */
    grid?: string[];
  };
}
